import {Alert, Button, Stack, Typography} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useApiManager} from "../../../components/utils/api";
import {EmpManagerRestControllerApiFactory, Vacation} from "arteco-api-client-ts";
import React, {useEffect, useState} from "react";
import {dateFromToString} from "../../../components/utils/dates";
import {btnSx, EmptyState, ListCard, Page, PageHeader, Panel, PersonAvatar, SkeletonRows} from "../../../components/kit";
import {vacationTypeLabel} from "../vacation/labels";

/**
 * Vacaciones ordinarias de las personas a cargo, pendientes de validar (#13877). Las de tipo especial
 * las aprueba RR. HH.; al validar, el solicitante recibe un correo de confirmación.
 */
export const VacationValidationPage = () => {
	const apiManager = useApiManager();
	const managerCtrl = apiManager.factory(EmpManagerRestControllerApiFactory);
	const [vacations, setVacations] = useState<Vacation[]>();

	const loadPendingVacations = () => {
		apiManager.execute(managerCtrl.getManagedPendingVacations())
			.then(vacations => setVacations(vacations));
	}

	useEffect(() => {
		loadPendingVacations();
	}, []);

	const approve = (vacation: Vacation) => {
		if (confirm("¿Seguro que desea validar esta solicitud?")) {
			apiManager.execute(managerCtrl.approveManagedVacations([vacation.id as number]))
				.then(() => loadPendingVacations());
		}
	};

	return (
		<Page maxWidth={960}>
			<PageHeader title="Vacaciones por validar"
						subtitle={vacations ? `${vacations.length} ${vacations.length === 1 ? "solicitud pendiente" : "solicitudes pendientes"}` : " "}/>

			<Alert severity="info" icon={<InfoOutlinedIcon/>} sx={{mb: 3, borderRadius: 2.5}}>
				Aquí se validan las solicitudes ordinarias de vacaciones; las de tipo especial las aprueba RR. HH.
				Al validar, el solicitante recibe un correo de confirmación.
			</Alert>

			<Panel>
				{!vacations ? <SkeletonRows rows={3} height={64}/> : vacations.length === 0 ? (
					<EmptyState icon={<CheckCircleRoundedIcon color="success"/>} title="No hay vacaciones ordinarias pendientes de validar."/>
				) : (
					<Stack spacing={1.5}>
						{vacations.map((vacation, idx) => {
							const name = vacation.person?.surnamesAndName;
							return (
								<ListCard key={idx}>
									<Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems={{sm: "center"}}>
										<Stack direction="row" spacing={1.5} alignItems="center" sx={{flexGrow: 1, minWidth: 0}}>
											<PersonAvatar name={name} size={36}/>
											<Stack sx={{minWidth: 0}}>
												<Typography variant="body2" fontWeight={600} noWrap>{name}</Typography>
												<Typography variant="caption" color="text.secondary">
													{dateFromToString(vacation.periodFrom)} – {dateFromToString(vacation.periodTo)}
													{vacation.type ? " · " + vacationTypeLabel(vacation.type) : ""}
												</Typography>
												{vacation.title && (
													<Typography variant="body2" color="text.secondary" sx={{mt: 0.25, overflowWrap: "anywhere"}}>
														{vacation.title}
													</Typography>
												)}
											</Stack>
										</Stack>
										<Button variant="contained" disableElevation onClick={() => approve(vacation)}
												sx={{...btnSx, alignSelf: {xs: "flex-start", sm: "auto"}, flexShrink: 0}}>
											Validar
										</Button>
									</Stack>
								</ListCard>
							);
						})}
					</Stack>
				)}
			</Panel>
		</Page>
	);
}
